import React from 'react'
import { formatTimeRange } from '../../utils/scheduleUtils'

// Each change type gets its own glyph and accent so the list can be skimmed:
// additions and removals are what people scan for, and colour carries that
// faster than reading every label.
const TYPE_STYLES = {
  'field': { icon: '✎', accent: 'text-blue-300', border: 'border-blue-500/30' },
  'artist-added': { icon: '+', accent: 'text-green-300', border: 'border-green-500/30' },
  'artist-updated': { icon: '🕒', accent: 'text-purple-300', border: 'border-purple-500/30' },
  'artist-removed': { icon: '−', accent: 'text-red-300', border: 'border-red-500/30' }
}

const formatDay = (value) => {
  if (!value) return ''
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00Z`)
  if (isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC'
  })
}

/**
 * The day is listed first and unconditionally.
 *
 * Without it, a flyer that only assigns artists to days — the common second
 * flyer for a multi-day festival — produced rows reading "— → —", because the
 * only thing that changed was the one field this never showed.
 */
const describeSetDetails = (details) => {
  if (!details) return '—'

  const parts = []

  const day = formatDay(details.performanceDate)
  if (day) parts.push(day)

  const time = formatTimeRange(details.startTime, details.endTime)
  if (time) parts.push(time)

  if (details.stage) parts.push(details.stage)

  return parts.length > 0 ? parts.join(' · ') : '—'
}

const ChangeRow = ({ change, checked, onToggle }) => {
  const style = TYPE_STYLES[change.type] || TYPE_STYLES.field

  return (
    <label
      className={`flex items-start gap-3 bg-white/5 border ${style.border} rounded-xl p-3 cursor-pointer hover:bg-white/10 transition-colors`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={() => onToggle(change.id)}
        className="mt-1 w-4 h-4 rounded accent-purple-500 flex-shrink-0"
      />

      <span className={`font-bold ${style.accent} w-4 text-center flex-shrink-0`}>{style.icon}</span>

      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-white">
          {change.label}
          {change.artistName && <span className="text-gray-300"> · {change.artistName}</span>}
        </div>

        {change.type === 'field' && (
          <div className="text-sm mt-1 flex items-center gap-2 flex-wrap">
            <span className="text-gray-400 line-through break-all">{change.before || 'empty'}</span>
            <span className="text-gray-500">→</span>
            <span className="text-white break-all">{change.after}</span>
          </div>
        )}

        {change.type === 'artist-added' && (
          <div className="text-sm text-gray-300 mt-1">
            {describeSetDetails(change.after)}
          </div>
        )}

        {change.type === 'artist-updated' && (
          <div className="text-sm mt-1 flex items-center gap-2 flex-wrap">
            <span className="text-gray-400 line-through">{describeSetDetails(change.before)}</span>
            <span className="text-gray-500">→</span>
            <span className="text-white">{describeSetDetails(change.after)}</span>
          </div>
        )}

        {change.type === 'artist-removed' && (
          <div className="text-sm text-red-200/80 mt-1">
            Not on this flyer — accepting removes them from the lineup.
          </div>
        )}
      </div>
    </label>
  )
}

export default ChangeRow
